import * as React from "react";

interface SidebarContextType {
  isOpen: boolean;
  openSideBar: () => void;
  closeSideBar: () => void;
  toggleSideBar: () => void;
  mainContent: React.RefObject<HTMLDivElement>;
  sideBarRef: React.RefObject<HTMLDivElement>;
  handleClickOutside: (event: MouseEvent) => void;
}

const SidebarContext = React.createContext<SidebarContextType | undefined>(
  undefined
);

export const useSidebar = () => {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
};

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const mainContent = React.useRef<HTMLDivElement>(null);
  const sideBarRef = React.useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = React.useState(false);

  const openSideBar = () => {
    setIsOpen(true);
    sideBarRef.current?.classList.add("open");
    const sideBarRender = document.getElementById("side-bar-render");
    sideBarRender?.classList.add("open");
    mainContent.current?.classList.add("open");
  };

  const closeSideBar = () => {
    setIsOpen(false);
    sideBarRef.current?.classList.remove("open");
    mainContent.current?.classList.remove("open");
    const sideBarRender = document.getElementById("side-bar-render");
    sideBarRender?.classList.remove("open");
  };

  const toggleSideBar = () => {
    if (isOpen) {
      closeSideBar();
    } else {
      openSideBar();
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (isOpen && !sideBarRef.current?.contains(event.target as Node)) {
      closeSideBar();
    }
  };

  const value = {
    isOpen,
    openSideBar,
    closeSideBar,
    toggleSideBar,
    mainContent,
    sideBarRef,
    handleClickOutside
  };

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
};
